import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ArrowRight, Calendar, Eye } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// Latest news section - replaces old CTA
// Mobile: stacked cards; desktop: 3-col grid
const LatestNews = () => {
  const { isDarkMode } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(
          'https://api.fsgu.co.za/get-posts.php?status=published&limit=3'
        );
        const data = await res.json();
        if (mounted && data.success && data.data) {
          setPosts(data.data);
        }
      } catch (err) {
        console.error('Latest news load error:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').slice(0, 140);
  };

  return (
    <section className={`py-12 sm:py-16 lg:py-24 ${
      isDarkMode
        ? 'bg-gradient-to-br from-fsgu-green-900/10 to-fsgu-gray-900'
        : 'bg-gradient-to-br from-fsgu-green-50 to-fsgu-silver-50'
    }`}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 sm:mb-10"
        >
          <div>
            <div className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-3 sm:mb-4 ${
              isDarkMode
                ? 'bg-fsgu-green-500/10 text-fsgu-green-400 border border-fsgu-green-500/20'
                : 'bg-fsgu-green-100 text-fsgu-green-700 border border-fsgu-green-200'
            }`}>
              <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              Latest News
            </div>
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold font-cursive mb-1 sm:mb-2 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Stay in the loop
            </h2>
            <p className={`text-base sm:text-lg ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Announcements, tournament updates, and stories from the clubs
            </p>
          </div>
          <Link
            to="/news"
            className={`inline-flex items-center text-sm font-medium transition-colors self-start group ${
              isDarkMode
                ? 'text-fsgu-green-400 hover:text-fsgu-green-300'
                : 'text-fsgu-green-600 hover:text-fsgu-green-700'
            }`}
          >
            All news
            <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        {loading ? (
          <div className="h-48 sm:h-64 flex items-center justify-center">
            <div className={`inline-block animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-4 border-solid border-current border-r-transparent ${
              isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
            }`} />
          </div>
        ) : posts.length === 0 ? (
          <div className={`text-center py-12 sm:py-16 rounded-2xl ${
            isDarkMode ? 'bg-fsgu-gray-800/50' : 'bg-white/50'
          }`}>
            <FileText className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 ${
              isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
            }`} />
            <p className={`text-sm sm:text-base ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              No news posts yet. Check back soon!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/news/${post.slug || post.id}`}
                  className={`group block h-full rounded-xl sm:rounded-2xl border overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-800 border-fsgu-gray-700 hover:border-fsgu-green-500/50'
                      : 'bg-fsgu-white border-fsgu-silver-200 hover:border-fsgu-green-500/50'
                  }`}
                >
                  {post.featured_image ? (
                    <div className="relative h-40 sm:h-48 overflow-hidden">
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {post.category_name && (
                        <div className="absolute top-2.5 sm:top-3 left-2.5 sm:left-3">
                          <span
                            className="inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium text-white backdrop-blur-sm border border-white/20"
                            style={{
                              backgroundColor: post.category_color
                                ? `${post.category_color}dd`
                                : 'rgba(34, 197, 94, 0.85)'
                            }}
                          >
                            {post.category_name}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`h-40 sm:h-48 flex items-center justify-center ${
                      isDarkMode
                        ? 'bg-gradient-to-br from-fsgu-green-900/40 to-fsgu-gray-700'
                        : 'bg-gradient-to-br from-fsgu-green-100 to-fsgu-silver-100'
                    }`}>
                      <FileText className={`w-10 h-10 sm:w-12 sm:h-12 ${
                        isDarkMode ? 'text-fsgu-green-500/40' : 'text-fsgu-green-500/60'
                      }`} />
                    </div>
                  )}

                  <div className="p-4 sm:p-5">
                    <h3 className={`text-base sm:text-lg font-bold mb-1.5 sm:mb-2 line-clamp-2 group-hover:text-fsgu-green-500 transition-colors ${
                      isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                    }`}>
                      {post.title}
                    </h3>

                    <p className={`text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2 ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      {post.excerpt || stripHtml(post.content)}
                    </p>

                    <div className={`flex items-center justify-between text-[10px] sm:text-xs pt-3 sm:pt-4 border-t ${
                      isDarkMode
                        ? 'text-fsgu-silver-500 border-fsgu-gray-700'
                        : 'text-fsgu-silver-500 border-fsgu-silver-200'
                    }`}>
                      <div className="flex items-center gap-1 sm:gap-1.5">
                        <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        {formatDate(post.published_at || post.created_at)}
                      </div>
                      {post.views > 0 && (
                        <div className="flex items-center gap-1 sm:gap-1.5">
                          <Eye className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          {post.views}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default LatestNews;